import { assert } from "@faro-lotv/foundation";
import { computeOrthophotoCamera } from "@faro-lotv/lotv";
import {
  Box3,
  Matrix4,
  OrthographicCamera,
  Quaternion,
  Sphere,
  Vector3,
} from "three";
import { cylinderNormal } from "./cylinder-surface";
import type { SurfaceSelection } from "./surface-selection-pass";
import { getFullDeepLinkURL } from "@/components/common/deep-link/deep-link-encoding";
import {
  encodeCameraParameters,
  getCameraParameters,
} from "@/components/r3f/utils/camera-parameters";
import { SceneFilter } from "@/types/scene-filter";

export const SURFACE_REPORT_IMAGE_SIZE = { width: 1600, height: 1000 };
const FRAME_MARGIN = 1.12;
const MIN_EXTENT = 0.01;

/**
 * @param selection Surface to frame without changing its selection mask.
 * @param viewer Current view position and up direction, used to choose the visible side.
 * @param sourceBounds Optional world-space source bounds for unclipped context depth.
 * @param view Surface view or a cylinder-axis projection with a circular fitted outline.
 * @returns Independent orthographic camera and its exact view target.
 */
export function surfaceReportCamera(
  selection: Pick<
    SurfaceSelection,
    "positions" | "origin" | "plane" | "cylinder"
  > & { tolerance?: number },
  viewer: { position: Vector3; up: Vector3 },
  sourceBounds?: Box3,
  view: "surface" | "cylinder-axis" = "surface",
): { camera: OrthographicCamera; target: Vector3 } {
  assert(
    selection.positions.length > 0,
    "The surface has no reconstruction geometry",
  );
  assert(
    view !== "cylinder-axis" || selection.cylinder,
    "An axial view needs a cylinder",
  );
  const axial = view === "cylinder-axis";
  const normal = selection.cylinder
    ? cylinderNormal(selection.cylinder, selection.origin)
    : new Vector3(
        selection.plane.x,
        selection.plane.y,
        selection.plane.z,
      ).normalize();
  if (axial && selection.cylinder) normal.copy(selection.cylinder.axis);
  assert(normal.lengthSq() > 0, "The surface has no viewing direction");
  if (
    !selection.cylinder &&
    viewer.position.clone().sub(selection.origin).dot(normal) < 0
  ) {
    normal.negate();
  }
  const vertical = selection.cylinder
    ? selection.cylinder.axis.clone()
    : viewer.up.clone().projectOnPlane(normal);
  if (axial && selection.cylinder) vertical.copy(selection.cylinder.radial);
  if (vertical.lengthSq() < MIN_EXTENT) {
    vertical.set(0, 1, 0).projectOnPlane(normal);
  }
  if (vertical.lengthSq() < MIN_EXTENT) {
    vertical.set(1, 0, 0).projectOnPlane(normal);
  }
  vertical.normalize();
  const horizontal = new Vector3().crossVectors(vertical, normal).normalize();
  const basis = new Matrix4().makeBasis(horizontal, vertical, normal);
  const bounds = new Box3();
  const point = new Vector3();
  for (let offset = 0; offset < selection.positions.length; offset += 3) {
    point.fromArray(selection.positions, offset);
    bounds.expandByPoint(
      new Vector3(
        point.dot(horizontal),
        point.dot(vertical),
        point.dot(normal),
      ),
    );
  }
  const target = bounds
    .getCenter(new Vector3())
    .applyMatrix4(basis)
    .add(selection.origin);
  const size = bounds
    .getSize(new Vector3())
    .add(
      axial
        ? new Vector3(
            2 * (selection.tolerance ?? 0),
            2 * (selection.tolerance ?? 0),
            0,
          )
        : new Vector3(),
    )
    .max(new Vector3(MIN_EXTENT, MIN_EXTENT, MIN_EXTENT))
    .multiplyScalar(FRAME_MARGIN);
  const aspect =
    SURFACE_REPORT_IMAGE_SIZE.width / SURFACE_REPORT_IMAGE_SIZE.height;
  size.x = Math.max(size.x, size.y * aspect);
  size.y = size.x / aspect;
  if (sourceBounds && !sourceBounds.isEmpty()) {
    const sphere = sourceBounds.getBoundingSphere(new Sphere());
    size.z = Math.max(
      size.z,
      2 * (sphere.radius + sphere.center.distanceTo(target)),
    );
  }
  const camera = computeOrthophotoCamera(
    {
      position: target,
      quaternion: new Quaternion().setFromRotationMatrix(basis),
      size,
    },
    new Vector3(0, 0, -1),
    new Vector3(0, 1, 0),
    new Vector3(1, 0, 0),
  );
  camera.up.copy(vertical);
  camera.updateProjectionMatrix();
  camera.updateMatrixWorld(true);
  return { camera, target };
}

/**
 * @param cloudId Source point-cloud stream.
 * @param camera The report's orthographic camera.
 * @param target The report's view target.
 * @returns A frontend deep link to the same camera, independent of transient wand masks.
 */
export function surfaceReportLink(
  cloudId: string,
  camera: OrthographicCamera,
  target: Vector3,
): string {
  return getFullDeepLinkURL({
    id: cloudId,
    mode: "overview",
    scene: SceneFilter.PointCloud,
    camera: encodeCameraParameters(getCameraParameters(camera, target)),
    cameraFrame: JSON.stringify({
      up: camera.up.toArray(),
      width: (camera.right - camera.left) / camera.zoom,
    }),
  }).toString();
}
